export const DAY_START_HOUR = 8;
export const DAY_END_HOUR = 20;
export const QUARTER_MINUTES = 15;

/**
 * All date helpers below operate in the browser's LOCAL timezone.
 * That way what the user sees (week boundaries, day cells, block positions) matches
 * their clock. The resulting ranges are then converted to UTC ISO strings at query
 * time via `.toISOString()` — which correctly translates "local week" into the
 * UTC instant boundaries the database needs.
 */

/** Monday 00:00:00 to Sunday 23:59:59.999 of the given date's week (local, ISO/European). */
export function getWeekRange(date: Date): { start: Date; end: Date } {
  const day = date.getDay(); // 0 = Sun, 1 = Mon, ... (local)
  const daysFromMonday = day === 0 ? 6 : day - 1;
  const start = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() - daysFromMonday,
    0, 0, 0, 0,
  );
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

/** 00:00:00.000 to 23:59:59.999 of the given date (local). */
export function getDayRange(date: Date): { start: Date; end: Date } {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
  const end = new Date(start);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

/** Rounds date to nearest QUARTER_MINUTES (ms-based, timezone-agnostic). */
export function snapToQuarter(date: Date): Date {
  const ms = date.getTime();
  const quarterMs = QUARTER_MINUTES * 60 * 1000;
  return new Date(Math.round(ms / quarterMs) * quarterMs);
}

/** Minutes from DAY_START_HOUR on the same date (local). Clamped to >= 0. */
export function minutesFromDayStart(date: Date): number {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const total = (hours - DAY_START_HOUR) * 60 + minutes;
  return Math.max(0, total);
}

export interface BlockRect {
  leftPct: number;
  widthPct: number;
}

/** Compute horizontal position of a block within a 12-hour day (08:00–20:00, local). */
export function computeBlockRect(params: { start: Date; durationMinutes: number }): BlockRect {
  const totalMinutes = (DAY_END_HOUR - DAY_START_HOUR) * 60;
  const leftMin = minutesFromDayStart(params.start);
  const leftPct = (leftMin / totalMinutes) * 100;
  const rawWidthPct = (params.durationMinutes / totalMinutes) * 100;
  const widthPct = Math.min(Math.max(rawWidthPct, 2), 100 - leftPct);
  return { leftPct, widthPct };
}

/**
 * Given a calendar day and a horizontal position within the day cell (0..1),
 * return the local Date at that hour+minute inside the visible window. Snaps
 * to the nearest QUARTER_MINUTES. Clamps out-of-range fractions to the window.
 */
export function computeDropTime(day: Date, fraction: number): Date {
  const clamped = Math.max(0, Math.min(1, fraction));
  const totalMinutes = (DAY_END_HOUR - DAY_START_HOUR) * 60;
  const offsetMinutes = clamped * totalMinutes;
  const totalFromDayStart = DAY_START_HOUR * 60 + offsetMinutes;
  const hours = Math.floor(totalFromDayStart / 60);
  const minutes = Math.round(totalFromDayStart % 60);
  const d = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hours, minutes, 0, 0);
  return snapToQuarter(d);
}

/** True if both dates fall on the same local calendar day. */
export function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Current-time horizontal position in percent (0..100) within the 12-hour window, or null if outside. */
export function currentTimePct(now: Date): number | null {
  const h = now.getHours();
  const m = now.getMinutes();
  const totalMinutes = (DAY_END_HOUR - DAY_START_HOUR) * 60;
  const elapsed = (h - DAY_START_HOUR) * 60 + m;
  if (elapsed < 0 || elapsed >= totalMinutes) return null;
  return (elapsed / totalMinutes) * 100;
}

export interface OverlapInput {
  id: string;
  prestadorId: string | null;
  start: Date;
  durationMinutes: number;
}

/** Returns the set of block ids that overlap with at least one other block for the same prestador. */
export function detectOverlaps(blocks: OverlapInput[]): Set<string> {
  const result = new Set<string>();
  const byPrestador = new Map<string, OverlapInput[]>();
  for (const b of blocks) {
    if (!b.prestadorId) continue;
    if (!byPrestador.has(b.prestadorId)) byPrestador.set(b.prestadorId, []);
    byPrestador.get(b.prestadorId)!.push(b);
  }
  for (const list of byPrestador.values()) {
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const a = list[i];
        const c = list[j];
        const aEnd = a.start.getTime() + a.durationMinutes * 60000;
        const cEnd = c.start.getTime() + c.durationMinutes * 60000;
        const overlaps = a.start.getTime() < cEnd && c.start.getTime() < aEnd;
        if (overlaps) { result.add(a.id); result.add(c.id); }
      }
    }
  }
  return result;
}
